/**
 * Job Submission Validation Utility
 *
 * Validates that a job submission request meets all requirements before processing.
 * Extracted from AppContext.tsx for better testability and maintainability.
 *
 * @module utils/jobSubmission/validateSubmission
 */

import { QueuedFile } from '../../types';

export interface ValidationState {
  fileQueue: QueuedFile[];
  isEmailValid: boolean;
  isSubmitting: boolean;
}

export interface ValidationError {
  message: string;
  type: 'no_files' | 'invalid_email' | 'already_submitting';
}

/**
 * Validates job submission requirements
 *
 * @param state - Current validation state containing file queue and email status
 * @returns Error object if validation fails, null if validation passes
 *
 * @example
 * ```typescript
 * const error = validateSubmission({ fileQueue, isEmailValid, isSubmitting });
 * if (error) {
 *   setError(error.message);
 *   return;
 * }
 * // Proceed with submission
 * ```
 */
export function validateSubmission(
  state: ValidationState
): ValidationError | null {
  // Check for files in queue
  if (state.fileQueue.length === 0) {
    return {
      message: 'No files to submit',
      type: 'no_files'
    };
  }

  // Check email validity
  if (!state.isEmailValid) {
    return {
      message: 'Please enter a valid email address',
      type: 'invalid_email'
    };
  }

  // Check for concurrent submission
  if (state.isSubmitting) {
    return {
      message: 'Submission already in progress',
      type: 'already_submitting'
    };
  }

  // All validations passed
  return null;
}
